import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  buildThresholdDrafts,
  getPreferredUnits,
  isThresholdsDirty,
  isThresholdsValid,
  type MetricThresholdDrafts,
  type PreferredUnits,
} from '../metricThresholdDrafts';
import type { MetricThresholdGroup } from '@flowqa/shared';

export const useMetricThresholdDrafts = (
  thresholds: MetricThresholdGroup,
): {
  drafts: MetricThresholdDrafts;
  setDrafts: Dispatch<SetStateAction<MetricThresholdDrafts>>;
  preferredUnitsRef: RefObject<PreferredUnits>;
  resetDrafts: (nextThresholds: MetricThresholdGroup) => void;
  isDirty: boolean;
  isValid: boolean;
} => {
  const initialDrafts = useMemo(() => buildThresholdDrafts(thresholds), [thresholds]);
  const [drafts, setDrafts] = useState<MetricThresholdDrafts>(() => initialDrafts);
  const [baselineThresholds, setBaselineThresholds] = useState<MetricThresholdGroup>(thresholds);
  const preferredUnitsRef = useRef<PreferredUnits>(getPreferredUnits(initialDrafts));
  const resetDrafts = useCallback((nextThresholds: MetricThresholdGroup): void => {
    setBaselineThresholds(nextThresholds);
    const nextDrafts = buildThresholdDrafts(nextThresholds);
    preferredUnitsRef.current = getPreferredUnits(nextDrafts);
    setDrafts(nextDrafts);
  }, []);
  const isDirty = useMemo(
    () => isThresholdsDirty(drafts, baselineThresholds),
    [baselineThresholds, drafts],
  );
  const isValid = useMemo(() => isThresholdsValid(drafts), [drafts]);

  return { drafts, setDrafts, preferredUnitsRef, resetDrafts, isDirty, isValid };
};
